import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Alarm,
  Clock,
  DotsThreeVertical,
  MagnifyingGlass,
  Package,
  Warning,
  XCircle,
} from "@phosphor-icons/react"
import {
  Divider,
  InputGroup,
  Menu,
  MenuItem,
  OverflowMenu,
  Popover,
  SegmentedControl,
  Tag,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useCallback, useEffect, useState } from "react"
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { TableSummary } from "~/components/TableSummary"
import FilterTypes from "~/constants/FilterTypes"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { siteConfig } from "~/constants/config"
import { AppRoutes } from "~/constants/routes"
import {
  useAuthenticatedUserQuery,
  useGetSharedEntityQuery,
  useRemainingStockQuantityQuery,
  useStockManagementListQuery,
  useStockStorageSummaryQuery,
} from "~/generated/graphql"
import { StockFilter, StockManagementFilters } from "./StockManagementFilters"

const StockManagement = () => {
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState(params.get("search") || "")
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250)
  const [viewPackaging] = useLocalStorage({ key: "viewPackaging" })
  const isPackaging = viewPackaging === "2"
  const page = Number(params.get("page")) || 1

  const [filter, setFilter] = useState<StockFilter>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.stockFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.stockFilter, JSON.stringify(filter))
  }, [filter])
  const navigate = useNavigate()

  const [{ data: authData }] = useAuthenticatedUserQuery()
  const me = authData?.me

  const [{ data: commodityData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteCommodity,
      codename: filter?.com_commodity?.codename,
    },
    pause: !filter || !filter?.com_commodity?.id,
  })

  const [{ data: varietyData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteVariety,
      codename: filter?.com_variety?.codename,
    },
    pause: !filter || !filter?.com_variety?.id,
  })

  const [{ data: gradeData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteGrade,
      codename: filter?.com_grade?.codename,
    },
    pause: !filter || !filter?.com_grade?.id,
  })

  const [{ data, fetching }] = useStockManagementListQuery({
    variables: {
      filter: {
        department_codenames: filter?.oth_department
          ? [filter?.oth_department?.codename]
          : me?.agent?.departments?.map(dept => dept?.department?.codename),
        commodity_id: filter?.com_commodity?.id
          ? commodityData?.setu_trade_shared_entity?.[0]?.id
          : undefined,
        commodity_variety_id: filter?.com_variety?.id
          ? varietyData?.setu_trade_shared_entity?.[0]?.id
          : undefined,
        grade_id: filter?.com_grade?.id
          ? gradeData?.setu_trade_shared_entity?.[0]?.id
          : undefined,
        storage_location_id: filter?.oth_storage_location?.id,
        material_kind: isPackaging ? "packaging" : "commodity",
      },
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      from: filter?.oth_date_from
        ? dayjs(filter?.oth_date_from).format("YYYY-MM-DD")
        : undefined,
      till: filter?.oth_date_to
        ? dayjs(filter?.oth_date_to).format("YYYY-MM-DD")
        : undefined,
    },
    requestPolicy: "network-only",
  })

  const [{ data: stockSummary, fetching: summaryFetching }] =
    useStockStorageSummaryQuery({
      variables: {
        filters: {
          department_codenames: filter?.oth_department
            ? [filter?.oth_department?.codename]
            : me?.agent?.departments?.map(dept => dept?.department?.codename),
          commodity_id: filter?.com_commodity?.id
            ? commodityData?.setu_trade_shared_entity?.[0]?.id
            : undefined,
          commodity_variety_id: filter?.com_variety?.id
            ? varietyData?.setu_trade_shared_entity?.[0]?.id
            : undefined,
          grade_id: filter?.com_grade?.id
            ? gradeData?.setu_trade_shared_entity?.[0]?.id
            : undefined,
          material_kind: isPackaging ? "packaging" : "commodity",
        },
        from: filter?.oth_date_from
          ? dayjs(filter?.oth_date_from).format("YYYY-MM-DD")
          : undefined,
        till: filter?.oth_date_to
          ? dayjs(filter?.oth_date_to).format("YYYY-MM-DD")
          : undefined,
      },
      requestPolicy: "network-only",
    })

  const totalStocks = stockSummary?.storage_summary?.total_stock_items || 0

  const summaryCards = [
    {
      id: "total",
      title: "Total Stock Items",
      value: stockSummary?.storage_summary?.total_stock_items || 0,
      helper: "",
      intentClass: "text-green-300",
      iconBgClass: "bg-[#F6FAE3]",
      icon: Package,
    },
    {
      id: "low",
      title: "Low Stock Items",
      value: stockSummary?.storage_summary?.low_stock_items || 0,
      helper: "Need Attention",
      intentClass: "text-amber-500",
      iconBgClass: "bg-amber-100",
      icon: Warning,
    },
    {
      id: "out",
      title: "Out of Stock",
      value: stockSummary?.storage_summary?.out_of_stock || 0,
      helper: "Critical",
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      icon: XCircle,
    },
    {
      id: "expiring_soon",
      title: "Expiring Soon",
      value: stockSummary?.storage_summary?.expiring_soon || 0,
      helper: "Next 10 days",
      intentClass: "text-[#F54900]",
      iconBgClass: "bg-[#FFF7ED]",
      icon: Clock,
    },
    {
      id: "expired",
      title: "Expired",
      value: stockSummary?.storage_summary?.expired_stock || 0,
      helper: "Last 2 days",
      intentClass: "text-red-500",
      iconBgClass: "bg-red-100",
      icon: Alarm,
    },
  ]

  const COLUMNS = [
    {
      id: "expander",
      Header: "",
      Cell: ({ row }: any) =>
        row.depth === 0 ? (
          <span
            {...row.getToggleRowExpandedProps({
              style: {
                paddingLeft: `${row.depth * 1.5}rem`,
              },
            })}
          >
            {row.isExpanded ? "▾" : "▸"}
          </span>
        ) : null,
    },
    {
      Header: "Material Name",
      accessor: "commodity.name",
      Cell({ value, row: { original } }) {
        return (
          <Cell
            title={value}
            subtitle={
              <span className="flex gap-2 items-center text-xs">
                <span className="text-gray-500">{original.variety?.name}</span>
              </span>
            }
          />
        )
      },
    },
    {
      Header: "Grade",
      accessor: "grade.name",
      Cell({ value }: { value: string }) {
        return (
          <Cell
            title={
              <Tag
                variant="minimal"
                intent="info"
                className={classNames("!rounded-xl")}
              >
                {value}
              </Tag>
            }
          />
        )
      },
    },

    {
      Header: "Storage Location",
      accessor: "storageLocation",
      Cell({ value }) {
        return value ? (
          <Cell
            title={
              <Tag
                intent="info"
                className={classNames("inline-block !rounded-xl !b-500")}
              >
                {value}
              </Tag>
            }
          />
        ) : (
          "--"
        )
      },
    },

    {
      Header: "Opening Stock",
      accessor: "total_opening_stock_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-green-400 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "Consume Stock",
      accessor: "total_consumed_stock_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-green-400 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "Closing Stock",
      accessor: "total_closing_stock_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-brown-400 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "Actual Stock",
      accessor: "actual_stock_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-blue-300 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "Virtual Stock",
      accessor: "virtual_stock_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-yellow-600 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "MSQ Units / KG",
      accessor: "minimum_selling_quantity_kg",
      Cell({ value }: { value: number }) {
        return (
          <Cell
            titleClassName={classNames("!text-brown-400 font-600")}
            title={`${value?.toLocaleString() || 0} kg`}
          />
        )
      },
    },
    {
      Header: "Material Status",
      accessor: "last_updated",
      Cell({ value }: { value: string }) {
        const lastMovementDays = dayjs().diff(value, "days")
        return value ? (
          <Cell
            title={
              <Tag
                size="sm"
                variant="outline"
                className="!rounded-xl"
                intent="warning"
              >
                {`Last Movt. ${
                  lastMovementDays ? lastMovementDays + " days" : " Today"
                }`}
              </Tag>
            }
          />
        ) : (
          "--"
        )
      },
    },
    {
      Header: "Offers",
      accessor: "offers",
      Cell({ value }) {
        return (
          <Cell
            title={
              <OverflowMenu
                items={value || []}
                getTitle={item => `#${item.id}`}
                getVisibleProps={item => {
                  return {
                    className:
                      "!bg-[#ED7500] !rounded-sm !text-white !border-none",
                    onClick: () => {
                      navigate(`/d/patti/${item.id}?type=offer`)
                    },
                  }
                }}
                getHiddenProps={item => {
                  return {
                    className: "!bg-white !text-[#ED7500]",
                    onClick: () => {
                      navigate(`/d/patti/${item.id}?type=offer`)
                    },
                  }
                }}
                showTooltip
                tooltipContent={item => {
                  return (
                    <div>
                      {dayjs(item?.created_at).format("DD MMM 'YY | hh:mm A")}
                      <br />
                      Qty.{(item?.quantity || 0).toLocaleString()} |
                      {inr(item?.total_amount || 0)}
                    </div>
                  ) as any
                }}
              />
            }
          />
        )
      },
    },
    {
      Header: "Demands",
      accessor: "demands",
      Cell({ value }) {
        return (
          <Cell
            title={
              <OverflowMenu
                items={value || []}
                getTitle={item => `#${item.id}`}
                getVisibleProps={item => {
                  return {
                    className:
                      "!bg-[#7B61FF] !rounded-sm !text-white !border-none",
                    onClick: () => {
                      navigate(`/d/patti/${item.id}?type=demand`)
                    },
                  }
                }}
                getHiddenProps={item => {
                  return {
                    className: "!bg-white !text-[#7B61FF]",
                    onClick: () => {
                      navigate(`/d/patti/${item.id}?type=demand`)
                    },
                  }
                }}
                showTooltip
                tooltipContent={item => {
                  return (
                    <div>
                      {dayjs(item?.created_at).format("DD MMM 'YY | hh:mm A")}
                      <br />
                      Qty.{(item?.quantity || 0).toLocaleString()} |
                      {inr(item?.total_amount || 0)}
                    </div>
                  ) as any
                }}
              />
            }
          />
        )
      },
    },
    {
      Header: "",
      id: "actions",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [params, setParams] = useSearchParams()
        const navigate = useNavigate()
        const location = useLocation()
        const [isShown, setShown] = useState(false)

        return (
          <Popover
            onShow={() => setShown(true)}
            minimal
            arrow={false}
            className="p-0"
            trigger="click"
            interactive
            animation="fade"
            theme="light-border"
            placement="bottom"
            content={
              <Menu className="max-h-fit">
                <h1 className="font-600 text-gray-800 p-1">Action</h1>
                <MenuItem
                  onClick={() => {
                    setParams(params)
                    navigate(
                      `/d/patti/new?type=demand&gradeId=${
                        original.grade?.id
                      }&commodityId=${original.commodity?.id}&varietyId=${
                        original.variety?.id
                      }&material_kind=${
                        isPackaging ? "packaging" : "commodity"
                      }`
                    )
                  }}
                >
                  Raise Demand
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(
                      `/d/patti/new?type=offer&gradeId=${
                        original.grade?.id
                      }&commodityId=${original.commodity?.id}&varietyId=${
                        original.variety?.id
                      }&material_kind=${
                        isPackaging ? "packaging" : "commodity"
                      }`
                    )
                  }}
                >
                  Raise Offer
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    return
                  }}
                  hidden
                  className="text-red"
                >
                  Write Off/Discard
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded">
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  const CHILD_COLUMNS = [
    {
      Header: "Created On",
      accessor: "created_on",
      Cell({ value }: { value: string }) {
        if (!value) return <Cell title="-" />
        return <Cell title={dayjs(value).format("DD MMM 'YY")} />
      },
    },
    {
      Header: "Storage Patti",
      accessor: "id",
      Cell({ value }: { value: string }) {
        return <Cell title={`#${value}`} />
      },
    },
    {
      Header: "Batch No.",
      accessor: "batch_number",
      Cell({ value }: { value: string }) {
        if (!value) return "--"
        return <Cell title={value} />
      },
    },
    {
      Header: "Material Type",
      accessor: "material",
      Cell({ value, row: { original } }) {
        return (
          <Cell
            title={value}
            subtitle={
              original?.materialCodeName ? (
                <Tag
                  size="sm"
                  variant="minimal"
                  className="!rounded-xl"
                  intent="info"
                >
                  {original?.materialCodeName}
                </Tag>
              ) : undefined
            }
          />
        )
      },
    },
    {
      Header: "Storage Location",
      accessor: "storage_location",
      Cell({ value }: { value: string }) {
        return (
          <Cell
            title={
              <Tag
                size="sm"
                variant="minimal"
                className="!rounded-xl"
                intent="info"
              >{`Storage #${value}`}</Tag>
            }
          />
        )
      },
    },
    {
      Header: "Ref Patti’s",
      accessor: "ref_pattis",
      Cell({ value }: { value: string }) {
        if (!value) {
          return "--"
        }
        return (
          <Cell
            title={
              <Tag size="sm" intent="info">
                {value}
              </Tag>
            }
          />
        )
      },
    },
    {
      Header: "Opening Stock",
      accessor: "opening_stock_kg",
      Cell({ value }: { value: number }) {
        if (!value) {
          return "--"
        }
        return (
          <Cell
            titleClassName="!text-green-400 font-600"
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "Consume Stock",
      accessor: "consumed_stock_kg",
      Cell({ value }: { value: number }) {
        if (!value) {
          return "--"
        }
        return (
          <Cell
            titleClassName="!text-green-400 font-600"
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "Closing Stock",
      accessor: "closing_stock_kg",
      Cell({ value }: { value: number }) {
        if (!value) {
          return "--"
        }
        return (
          <Cell
            titleClassName="!text-brown-400 font-600"
            title={`${value?.toLocaleString()} kg`}
          />
        )
      },
    },
    {
      Header: "STD Holding Time/Days",
      accessor: "std_holding_days",
      Cell({ value }: { value: number }) {
        if (!value) return "--"
        return (
          <Cell
            titleClassName="!text-blue-300 font-600"
            title={`${value} days`}
          />
        )
      },
    },
    {
      Header: "Storage Status",
      accessor: "storage_status",
      Cell({ value }) {
        if (!value) return "--"
        return (
          <Cell
            title={
              <Tag size="sm" variant="outline" className="!rounded-xl">
                {value}
              </Tag>
            }
          />
        )
      },
    },
    {
      Header: "Outward/Action",
      accessor: "outward_actions",
      Cell({ value }: { value?: string[] }) {
        const ids =
          value?.map(item => item.split(" ")[0]?.replaceAll("#", "")) || []
        return (
          <Cell
            title={
              <OverflowMenu
                items={ids}
                getTitle={item => (
                  <Link to={AppRoutes.tradeRequests + "/" + item}>
                    {"#" + item}
                  </Link>
                )}
                getItemProps={() => ({
                  className: "!text-green border-none underline",
                })}
              />
            }
          />
        )
      },
    },
    {
      Header: "",
      id: "actions",
      accessor: "updated_at",
      Cell({ row: { original } }) {
        const [isShown, setShown] = useState(false)
        const offers = original?.offers
        const [{ data }] = useRemainingStockQuantityQuery({
          variables: {
            where: {
              kind: { _eq: "order" },
              trade_items: {
                variety_grade_id: { _eq: original?.varietyGradeId },
              },
              current_status: {
                id: {
                  _eq: "Verified",
                },
              },
              offer_id: {
                _in: offers?.map(offer => offer.id),
              },
            },
          },
          pause: !offers.length,
        })

        const orderPatti = data?.trade_aggregate?.nodes?.[0]

        const remainingQuantity =
          data?.trade_aggregate?.aggregate?.sum?.deducted_quantity_remaining
        return (
          <Popover
            onShow={() => setShown(true)}
            minimal
            arrow={false}
            className="p-0 !z-[100]"
            trigger="click"
            interactive
            animation="fade"
            theme="light-border"
            placement="bottom"
            content={
              <Menu className="max-h-fit">
                <h1 className="font-600 text-gray-800 p-1">Action</h1>
                <MenuItem
                  onClick={() => {
                    if (!orderPatti) return
                    setParams(params)
                    navigate(`/d/trade-requests/${orderPatti?.id}?new=true`)
                  }}
                  disabled={!remainingQuantity || !orderPatti}
                >
                  Hishob Patti
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    return
                  }}
                  hidden
                  className="text-red"
                >
                  Write Off/Discard
                </MenuItem>
              </Menu>
            }
          >
            <button className="hover:bg-gray-300 rounded">
              <DotsThreeVertical size={20} />
            </button>
          </Popover>
        )
      },
    },
  ]

  const renderRowSubComponent = useCallback(({ row }) => {
    const children = row?.original?.pattis || []
    if (!children.length) return null

    // Map parent's offers with trade_set to each child patti
    const childrenWithOffers = children.map((child: any) => ({
      ...child,
      offers: row?.original?.offers || [],
      varietyGradeId: row?.original?.grade?.id,
    }))

    return (
      <td colSpan={COLUMNS.length}>
        <TableView
          columns={CHILD_COLUMNS as any}
          data={childrenWithOffers}
          className="!overflow-visible"
          tableClassName="!m-0 !overflow-visible !p-0 !z-[0]"
          getHeaderProps={() => {
            return {
              className: "border-b-1 border-gray-300",
            }
          }}
          getCellProps={cell => {
            if (cell.column.id === "actions") {
              return {
                className: "sticky right-0 bg-white !z-[10]",
              }
            }
          }}
        />
      </td>
    )
  }, [])

  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"stock-management"}
          onChange={value => {
            if (value === "demand") {
              navigate(AppRoutes.demandManagement)
            } else if (value === "offer") {
              navigate(AppRoutes.offerManagement)
            }
          }}
          data={[
            {
              label: "Stock Mgnt.",
              value: "stock-management",
            },
            {
              label: "Offer",
              value: "offer",
            },
            {
              label: "Demand",
              value: "demand",
            },
          ].filter(b => b)}
        />
        <div className="w-[500px] flex items-center gap-3">
          <InputGroup
            className="w-full"
            leftElement={<MagnifyingGlass />}
            inputProps={{
              value: search,
              placeholder: "Search by storage patti",
              onChange(e) {
                const value = e.target.value
                setSearch(value)
                if (value) {
                  params.set("search", value)
                } else {
                  params.delete("search")
                }
                setParams(params)
              },
            }}
          />
          <StockManagementFilters filter={filter} setFilter={setFilter} />
        </div>
      </header>

      <section className="m-4 bg-white p-4 mb-0 rounded-t-xl">
        <div className="grid grid-cols-5 gap-4">
          <TableSummary summary={summaryCards} loading={summaryFetching} />
        </div>
      </section>

      <section className="mx-4 mb-2 bg-white rounded-b-xl border border-t-0 border-gray-200">
        <Divider />
        <TableView
          relative={false}
          className="!mt-0 overflow-x-auto"
          tableClassName="!mt-0 !min-h-[600px] max-h-[600px] overflow-y-scroll"
          isLoading={fetching}
          columns={COLUMNS as any}
          data={data?.storage_dashboard || []}
          meta={
            <span className="text-xs text-gray-500">{totalStocks} results</span>
          }
          paginationProps={{
            total: Math.ceil(totalStocks / siteConfig.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              params.set("search", "")
              setParams(params)
            },
          }}
          getHeaderProps={() => {
            return {
              className: "border-b-1 border-gray-300",
            }
          }}
          getCellProps={cell => {
            if (
              cell?.column?.id !== "expander" &&
              cell?.column?.id !== "actions"
            ) {
              return {
                className: " min-w-[150px] !z-[10]",
                onClick: () => {
                  cell.row.toggleRowExpanded()
                },
              }
            }
            if (cell.column.id === "actions") {
              return {
                className: "sticky right-0 bg-white !z-[10]",
              }
            }
          }}
          getRowProps={() => {
            return {
              className: "border-y-1 border-gray-300 border-solid ",
            }
          }}
          renderRowSubComponent={renderRowSubComponent}
        />
      </section>
    </>
  )
}

export default StockManagement
